import React from 'react';
import type { POSConventionProps } from './POSConvention.types';
import { ConventionIcon } from './icons';

/**
 * POSConvention - Componente de leyenda/convención para estados de mesas POS
 *
 * Muestra un indicador circular de color con su respectiva etiqueta según el diseño de Figma.
 * Se usa típicamente para crear leyendas que explican el significado de colores en el sistema POS.
 *
 * **Colores según Figma (node-id: 4418-40139):**
 *
 * | Estado | Light Mode | Dark Mode |
 * |--------|------------|-----------|
 * | Available | #0e79fd (azul) | #93d1fd (azul claro) |
 * | Occupied | #af460e (naranja) | #f5a927 (amarillo) |
 * | Reserved | #7e22ce (morado) | #c084fc (morado claro) |
 * | Out of Service | #3f3f46 (gris) | #a1a1aa (gris claro) |
 *
 * **Dimensiones según Figma:**
 * - Icono: 16x16px (círculo con fill)
 * - Texto: 12px (Paragraph/Tiny - text-xs)
 * - Gap: 4px (gap-1)
 * - Line-height: 16px (leading-4)
 *
 * Mejores prácticas implementadas:
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Colores exactos de Figma para Light y Dark mode
 * - Type safety con TypeScript estricto
 * - Accesibilidad con aria-label
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico (Paragraph Tiny)
 * @see docs/spacing.md - Sistema de espaciado
 *
 * @example
 * ```tsx
 * // Indicador de disponible
 * <POSConvention status="available" />
 *
 * // Indicador de ocupada
 * <POSConvention status="occupied" />
 *
 * // Leyenda completa en contenedor
 * <div className="p-5 rounded-lg border border-dashed border-primary-300">
 *   <div className="flex flex-col gap-9">
 *     <POSConvention status="available" />
 *     <POSConvention status="occupied" />
 *     <POSConvention status="reserved" />
 *     <POSConvention status="outOfService" />
 *   </div>
 * </div>
 * ```
 */
export const POSConvention: React.FC<POSConventionProps> = ({
  status = 'available',
  className = '',
}) => {
  // ===== CONFIGURACIÓN DE COLORES Y LABELS SEGÚN FIGMA =====
  // Colores extraídos del nodo 4418-40139 (Light) y 4418-40349 (Dark) de Figma
  // Variables de Figma: contentCustomPrimary, contentYellow, contentPurple, contentZinc
  const conventionConfig = {
    available: {
      lightColor: '#0e79fd', // Azul - contentCustomPrimary Light
      darkColor: '#93d1fd',  // Azul claro - contentCustomPrimary Dark
      label: 'Disponible',
    },
    occupied: {
      lightColor: '#af460e', // Naranja - contentOrange Light
      darkColor: '#f5a927',  // Amarillo - contentYellow Dark
      label: 'Ocupada',
    },
    reserved: {
      lightColor: '#7e22ce', // Morado - contentPurple Light
      darkColor: '#c084fc',  // Morado claro - contentPurple Dark
      label: 'Reservada',
    },
    outOfService: {
      lightColor: '#3f3f46', // Gris - contentZinc Light (zinc-700)
      darkColor: '#a1a1aa',  // Gris claro - contentZinc Dark (zinc-400)
      label: 'Fuera de Servicio',
    },
  };

  const config = conventionConfig[status];

  // ===== CLASES BASE =====
  const baseClasses = `
    inline-flex
    items-center
    gap-1
    shrink-0
  `;

  // ===== COMBINAR CLASES =====
  const finalClasses = [baseClasses, className]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <div
      className={finalClasses}
      role="presentation"
      aria-label={`Estado: ${config.label}`}
    >
      {/* Icono circular 16x16 con colores para Light y Dark mode */}
      <ConventionIcon lightColor={config.lightColor} darkColor={config.darkColor} />

      {/* Label de texto según Figma: Paragraph/Tiny 12px */}
      {/* Light: contentPrimary #18181b, Dark: contentPrimary #f4f4f5 */}
      <span
        className={`
          text-xs
          leading-4
          font-normal
          text-content-primary
          dark:text-dark-content-primary
          whitespace-nowrap
        `
          .replace(/\s+/g, ' ')
          .trim()}
      >
        {config.label}
      </span>
    </div>
  );
};
